import { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Tabs, Tab, Grid, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import SettingsIcon from '@mui/icons-material/Settings';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import FiberSmartRecordOutlinedIcon from '@mui/icons-material/FiberSmartRecordOutlined';
import MainCard from '../../../../components/MainCard';
import PickerField from '../../../../components/PickerField';
import PickerFieldTable from '../../../../components/PickerFieldTable';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useCompanyDataList } from '../../../../stores/companyStore';
import { useMasterVehicleList } from '../../../../stores/masterVehicleStore';
import { getTireEquipVehicle } from '../../../../stores/tireStore';
import { getAxleTypeByName } from '../../../../stores/axleTypeStore';
import usePickerPagination from '../../../../hooks/usePickerPagination';
import { fleetApi } from '../../../../api/axiosClient';
import FilterBuilder from '../../../../utils/filterBuilder';

import { AxleMap, parseAxleConfig } from './AxleMap';
import InstallForm from './InstallForm';
import RemoveForm from './RemoveForm';
import TabTireInspection from './TabTireInspection';
import TireActionList from './TireActionList';
import { useLanguage } from '../../../../utils/i18n';

const buildVehicleArgs = (state) => [state.page, state.pageSize, [], state.searchKey, state.searchValue];
const formatVehicleOption = (v) => ({
  value: v.id,
  label: `${v.license_plate} - ${v.join?.vehicle_type_name || v.vehicle_type_name || ''} ${v.join?.axle_type_name || ''}`,
  original: v,
  license_plate: v.license_plate,
  vehicle_type_name: v.vehicle_type_name || v.join?.vehicle_type_name,
  merk: v.merk,
  model: v.model,
  year: v.year
});

export default function TireCyclePage() {
  const { t } = useLanguage();
  const vehicleColumns = [
    { field: 'license_plate', headerName: t('tireHistorical.licensePlate', 'License Plate') },
    { field: 'vehicle_type_name', headerName: t('general.vehicleType', 'Vehicle Type') },
    { field: 'merk', headerName: t('specification.brand', 'Brand') },
    { field: 'model', headerName: t('general.model', 'Model') },
    { field: 'year', headerName: t('general.year', 'Year') }
  ];

  const [activeTab, setActiveTab] = useState(0);

  // Global State
  const [companyId, setCompanyId] = useState('');

  useEffect(() => {
    let defaultCompanyId = '';
    const stored = localStorage.getItem('companyId') || localStorage.getItem('company_id');
    if (stored) {
      defaultCompanyId = String(stored);
    } else {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const u = JSON.parse(userStr);
          if (u?.company) defaultCompanyId = String(u.company);
        } catch {}
      }
    }
    if (defaultCompanyId) setCompanyId(defaultCompanyId);
  }, []);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [vehicleDetails, setVehicleDetails] = useState(null);
  const [hmUnit, setHmUnit] = useState('');
  const [kmUnit, setKmUnit] = useState('');
  const [vehicleHistoricalData, setVehicleHistoricalData] = useState([]);
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));

  // Tire Selection State
  const [selectedTire, setSelectedTire] = useState(null);
  const [vehicleTiresData, setVehicleTiresData] = useState([]);

  // Axle Map State
  const [installedTires, setInstalledTires] = useState(null);
  const [rawAxleData, setRawAxleData] = useState(null);
  const [tireEquipVehicleId, setTireEquipVehicleId] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedTiresForRemove, setSelectedTiresForRemove] = useState([]);

  // Action View State for Install / Remove Tab
  const [activeActionView, setActiveActionView] = useState('list'); // 'list' | 'install' | 'remove'
  const [installQueue, setInstallQueue] = useState([]);
  const [removeQueue, setRemoveQueue] = useState([]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedPosition(null);
    setSelectedTire(null);
    setActiveActionView('list');
  };

  const handleInstallClick = () => {
    setActiveActionView('install');
    setSelectedPosition(null); // Wait for user to select an empty position
  };

  const handleRemoveClick = (tiresArray) => {
    // tiresArray contains objects with posCode and other tire details
    const selected = (tiresArray || [])
      .map((tire) => ({
        ...tire,
        posCode: tire.posCode
      }))
      .filter((t) => t.posCode); // Ensure posCode exists

    if (selected.length > 0) {
      setSelectedTiresForRemove(selected);
      setActiveActionView('remove');
    } else {
      setActiveActionView('list');
    }
  };

  const handleCancelAction = () => {
    setActiveActionView('list');
    setSelectedPosition(null);
    setSelectedTiresForRemove([]);
    setSelectedTire(null);
  };

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const vehiclePickerParams = usePickerPagination({
    fetchHook: useMasterVehicleList,
    buildArgs: buildVehicleArgs,
    formatOption: formatVehicleOption
  });

  const fetchAllHistoricalData = async (v) => {
    try {
      const cId = v.company_id || v.company || companyId;
      const license_plate = v.license_plate;
      let allData = [];
      let start = 0;
      const rowsPerPage = 1000;
      let hasMore = true;

      while (hasMore) {
        const payload = new FilterBuilder()
          .setPaging(start, rowsPerPage)
          .setOrder(['created_at'], false)
          .equal('company_id', cId)
          .like('license_plate', license_plate, 'AND')
          .build();

        const tzParam = `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;
        const res = await fleetApi.post(`/tire-transactions/search${tzParam}`, payload);
        const fetchedData = res?.data?.data || res?.data || [];
        allData = [...allData, ...fetchedData];

        const total = res?.data?.total || fetchedData.length;
        start += rowsPerPage;
        if (allData.length >= total || fetchedData.length === 0) {
          hasMore = false;
        }
      }

      // Sort exact descending by transaction_date, then created_at
      const sorted = allData.sort((a, b) => {
        const d1 = new Date(b.transaction_date) - new Date(a.transaction_date);
        if (d1 !== 0) return d1;
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setVehicleHistoricalData(sorted);
    } catch (e) {
      console.error(e);
      setVehicleHistoricalData([]);
    } finally {
    }
  };

  const limitInfo = useMemo(() => {
    const isBackdate = date ? dayjs(date).isBefore(dayjs(), 'day') : false;
    let minKm = 0,
      maxKm = Infinity,
      minHm = 0,
      maxHm = Infinity;
    let beforeTx = null,
      afterTx = null,
      errorRule = null;
    let allowedTx = ['FIT', 'REMOVE', 'INSPECTION', 'REPAIR', 'SCRAP'];

    if (!vehicleHistoricalData || vehicleHistoricalData.length === 0) {
      return { minKm, maxKm, minHm, maxHm, beforeTx, afterTx, errorRule, allowedTx, isBackdate, beforeData: null, afterData: null };
    }

    if (!isBackdate) {
      const latest = vehicleHistoricalData[0];
      minKm = latest?.km_unit || 0;
      minHm = latest?.hm_unit || 0;
      return { minKm, maxKm, minHm, maxHm, beforeTx, afterTx, errorRule, allowedTx, isBackdate, beforeData: latest, afterData: null };
    }

    // Backdate logic
    const selectedDay = dayjs(date).startOf('day');
    // First FIT validation rule removed.

    let beforeData = null;
    let afterData = null;

    // vehicleHistoricalData is sorted desc (newest first)
    for (let i = 0; i < vehicleHistoricalData.length; i++) {
      const tDay = dayjs(vehicleHistoricalData[i].transaction_date).startOf('day');
      if (tDay.isAfter(selectedDay)) {
        afterData = vehicleHistoricalData[i];
      } else {
        if (!beforeData) beforeData = vehicleHistoricalData[i];
      }
    }

    minKm = beforeData ? beforeData.km_unit : 0;
    maxKm = afterData ? afterData.km_unit : Infinity;
    minHm = beforeData ? beforeData.hm_unit : 0;
    maxHm = afterData ? afterData.hm_unit : Infinity;

    // Transaction type sequence rules have been removed as requested.
    // allowedTx remains all transaction types.
    if (beforeData && afterData && beforeData.km_unit === afterData.km_unit) {
      minKm = beforeData.km_unit;
      maxKm = beforeData.km_unit;
    }
    if (beforeData && afterData && beforeData.hm_unit === afterData.hm_unit) {
      minHm = beforeData.hm_unit;
      maxHm = beforeData.hm_unit;
    }

    return { minKm, maxKm, minHm, maxHm, beforeTx, afterTx, errorRule, allowedTx, isBackdate, beforeData, afterData };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleHistoricalData, date, selectedTire, vehicleTiresData]);

  const fetchTireEquip = async (v) => {
    try {
      const cId = v.company_id || v.company || companyId;

      let baseAxleConfig = null;
      if (v.join?.axle_type_name) {
        const axleTypeData = await getAxleTypeByName(v.join.axle_type_name);
        if (axleTypeData) {
          let configObj = axleTypeData.axle_configuration || axleTypeData.config;
          if (typeof configObj === 'string') {
            try {
              configObj = JSON.parse(configObj);
            } catch (e) {
              console.error(e);
            }
          }
          baseAxleConfig = configObj;
        }
      }

      const data = await getTireEquipVehicle(cId, v.id);
      let parsedPosition = null;

      if (data && data.length > 0) {
        const equipData = data[0];
        setTireEquipVehicleId(equipData.id);

        let currentPos = equipData.current_position;
        if (typeof currentPos === 'string') {
          try {
            currentPos = JSON.parse(currentPos);
          } catch (e) {
            console.error(e);
          }
        }

        if (currentPos && currentPos.unit_info) {
          const km = currentPos.unit_info.km_unit || 0;
          const hm = currentPos.unit_info.hm_unit || 0;
          setKmUnit(km.toString());
          setHmUnit(hm.toString());
        } else {
          setVehicleHistoricalData([]);
          setKmUnit('');
          setHmUnit('');
        }

        if (currentPos && currentPos.axle_info) {
          currentPos = currentPos.axle_info;
        }

        if (!currentPos || !currentPos.info) {
          let basePos = equipData.base_position;
          if (typeof basePos === 'string') {
            try {
              basePos = JSON.parse(basePos);
            } catch (e) {
              console.error(e);
            }
          }
          currentPos = basePos;
        }
        parsedPosition = currentPos;
      } else {
        setTireEquipVehicleId(null);
        setKmUnit('');
        setHmUnit('');
      }

      const installed = {};
      if (parsedPosition && parsedPosition.info) {
        parsedPosition.info.forEach((axle) => {
          (axle.positions || []).forEach((pos) => {
            if (pos.serial_number && pos.serial_number.trim() !== '') {
              installed[pos.code] = {
                serial_number: pos.serial_number,
                tire_item_id: pos.tire_item_id
              };
            }
          });
        });
      }

      setInstalledTires(installed);

      let finalAxleData = null;
      if (baseAxleConfig && baseAxleConfig.info) {
        finalAxleData = JSON.parse(JSON.stringify(baseAxleConfig));
        finalAxleData.info.forEach((axle) => {
          (axle.positions || []).forEach((pos) => {
            if (installed[pos.code]) {
              pos.serial_number = installed[pos.code].serial_number;
              pos.tire_item_id = installed[pos.code].tire_item_id;
            } else {
              pos.serial_number = '';
              pos.tire_item_id = 0;
            }
          });
        });
      } else {
        finalAxleData = parsedPosition;
      }
      setRawAxleData(finalAxleData);
    } catch (error) {
      console.error('Failed to fetch tire equip info', error);
      setTireEquipVehicleId(null);
      setRawAxleData(null);
      setInstalledTires(null);
    }
  };

  useEffect(() => {
    if (selectedVehicleId) {
      const opt = vehiclePickerParams.options.find((x) => x.value === selectedVehicleId);
      const v = opt ? opt.original : null;
      if (v) {
        setVehicleDetails(v);
        setHmUnit(v.hour_meter || '');
        setKmUnit(v.odometer || '');
        setSelectedPosition(null);
        setActiveActionView('list');
        setInstallQueue([]);
        setRemoveQueue([]);
        fetchTireEquip(v);
        fetchAllHistoricalData(v);
      }
    } else {
      setVehicleDetails(null);
      setHmUnit('');
      setKmUnit('');
      setTireEquipVehicleId(null);
      setRawAxleData(null);
      setInstalledTires(null);
      setSelectedPosition(null);
      setSelectedTiresForRemove([]);
      setSelectedTire(null);
      setActiveActionView('list');
      setInstallQueue([]);
      setRemoveQueue([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicleId]);

  const axles = useMemo(() => {
    if (rawAxleData) return parseAxleConfig(rawAxleData);
    return [];
  }, [rawAxleData]);

  const positionMap = useMemo(() => {
    const map = {};
    let idx = 1;
    axles.forEach((axle) => {
      axle.left.forEach((p) => {
        map[p.key] = { index: idx++, label: p.label };
      });
      axle.right.forEach((p) => {
        map[p.key] = { index: idx++, label: p.label };
      });
    });
    return map;
  }, [axles]);

  const temporaryInstalledTires = useMemo(() => {
    if (!installedTires) return null;
    let temp = { ...installedTires };

    removeQueue.forEach((item) => {
      if (temp[item.position]) {
        delete temp[item.position];
      }
    });

    installQueue.forEach((item) => {
      temp[item.position] = {
        serial_number: item.tireData.serial_number || item.tireData.batch_number,
        tire_item_id: item.tireData.tire_item_id || item.tireData.id,
        is_queued: true
      };
    });

    return temp;
  }, [installedTires, installQueue, removeQueue]);

  const isPositionFilled = (posKey) => !!(temporaryInstalledTires && temporaryInstalledTires[posKey]);

  const handlePositionClick = (posKey) => {
    if (activeTab === 0) {
      // In Install / Remove tab
      if (actionView === 'list') {
        if (isPositionFilled(posKey)) {
          setSelectedTire((prev) => (prev === posKey ? null : posKey));
          setSelectedPosition(null);
        } else {
          setSelectedPosition(posKey);
          setSelectedTire(null);
          setActiveActionView('install');
        }
      } else if (actionView === 'install') {
        // Only allow clicking empty positions while in install view
        if (!isPositionFilled(posKey)) setSelectedPosition(posKey);
      } else if (actionView === 'remove') {
        // Only allow clicking filled positions while in remove view
        if (isPositionFilled(posKey)) setSelectedPosition(posKey);
      }
    } else if (activeTab === 1) {
      // Inspection tab
      if (isPositionFilled(posKey)) {
        setSelectedTire((prev) => (prev === posKey ? null : posKey));
      }
      setSelectedPosition(posKey);
    }
  };

  const handleEmptyAreaClick = () => {
    if ((activeTab === 0 && actionView === 'list') || activeTab === 1) {
      setSelectedTire(null);
    }
  };

  const actionView = activeActionView || 'list';

  const refreshMap = () => {
    if (vehicleDetails) {
      fetchTireEquip(vehicleDetails);
      fetchAllHistoricalData(vehicleDetails);
    }
    setActiveActionView('list');
    setSelectedPosition(null);
    setSelectedTire(null);
    setInstallQueue([]);
    setRemoveQueue([]);
  };

  const handleTiresDataChange = (data) => {
    setVehicleTiresData(data);
  };

  const isValidHm = hmUnit !== '' && Number(hmUnit) >= limitInfo.minHm && Number(hmUnit) <= limitInfo.maxHm;
  const isValidKm = kmUnit !== '' && Number(kmUnit) >= limitInfo.minKm && Number(kmUnit) <= limitInfo.maxKm;
  const isHmKmValid = isValidHm && isValidKm && !limitInfo.errorRule;

  const renderAxleMap = () => (
    <MainCard
      title={t('tireCycle.axleMapVisualization', 'Axle Map Visualization')}
      content={false}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: 'none'
      }}
    >
      {vehicleDetails ? (
        <Box
          sx={{
            p: 2,
            bgcolor: '#fcfcfc',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: 0
          }}
        >
          <Box
            sx={{
              mb: 2,
              pb: 2,
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {vehicleDetails.license_plate}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {vehicleDetails.vehicle_type_name || vehicleDetails.join?.vehicle_type_name || '-'} • {vehicleDetails.merk || '-'} •{' '}
                {vehicleDetails.model || '-'} • {vehicleDetails.year || '-'}
              </Typography>
            </Box>
          </Box>
          <Grid container size={{ sm: 12 }} mb={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 0.5, color: '#0ea5e9' }}>
                  {t('tireCycle.steering', 'Steering')}
                </Typography>
                <SwapHorizIcon sx={{ fontSize: 16, color: '#0ea5e9' }} />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 0.5, color: '#334155' }}>
                  {t('tireCycle.driven', 'Driven')}
                </Typography>
                <SettingsIcon sx={{ fontSize: 16, color: '#334155' }} />
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 0.5, color: '#8b5cf6' }}>
                  {t('tireCycle.liftAxle', 'Lift Axle')}
                </Typography>
                <KeyboardDoubleArrowUpIcon sx={{ fontSize: 16, color: '#8b5cf6' }} />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 0.5, color: '#3995c0' }}>
                  {t('tireCycle.twinTire', 'Twin Tire')}
                </Typography>
                <FiberSmartRecordOutlinedIcon sx={{ fontSize: 16, color: '#3995c0' }} />
              </Box>
            </Grid>
          </Grid>
          {isHmKmValid ? (
            <Box sx={{ flexGrow: 1, overflowY: 'auto', display: 'flex', justifyContent: 'center', minHeight: 0, pb: 2 }}>
              <AxleMap
                axles={axles}
                positionMap={positionMap}
                isPositionFilled={isPositionFilled}
                selectedPosition={selectedPosition}
                selectedTire={selectedTire}
                selectedTireData={vehicleTiresData.find((t) => t.posCode === selectedTire)}
                handlePositionClick={handlePositionClick}
                onEmptyAreaClick={handleEmptyAreaClick}
              />
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center', flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body1" color="warning.main" fontWeight="bold">
                {t(
                  'tireCycle.provideValidHmKmAxleMap',
                  'Please provide valid HM and KM units (greater than or equal to the previous records) to view the Axle Map.'
                )}
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ p: 4, textAlign: 'center', flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {t('tireCycle.selectVehicleViewAxleMap', 'Please select a vehicle to view the Axle Map.')}
          </Typography>
        </Box>
      )}
    </MainCard>
  );

  return (
    <Box onClick={handleEmptyAreaClick}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          {t('tireCycle.tireCycle', 'Tire Cycle')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('tireCycle.tireCycleDesc', 'Manage tire installation, removal, and inspections.')}
        </Typography>
      </Box>

      {/* Global Filters */}
      <MainCard sx={{ mb: 1 }}>
        <Grid container spacing={3}>
          <Grid item size={{ xs: 12, md: 6 }}>
            <PickerField
              label={t('general.company', 'Company')}
              value={companyId}
              onChange={(val) => {
                setCompanyId(val ?? '');
                setSelectedVehicleId(null);
              }}
              options={companyPickerParams.options}
              loading={companyPickerParams.loading}
              onSearch={companyPickerParams.handleSearch}
              onScrollEnd={companyPickerParams.handleScrollEnd}
              getOptionId={(o) => o?.value ?? o?.id ?? ''}
              getOptionLabel={(o) => o?.label ?? o?.name ?? ''}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 6 }}>
            <PickerFieldTable
              label={t('tireCardHistory.vehicle', 'Vehicle')}
              {...vehiclePickerParams}
              value={selectedVehicleId}
              onChange={(val) => setSelectedVehicleId(val)}
              disabled={!companyId}
              columns={vehicleColumns}
              dialogTitle={t('tireCycle.selectVehicle', 'Select Vehicle')}
              multiSelect={false}
              getOptionId={(o) => o?.value ?? o?.id}
              getOptionLabel={(o) => o?.label ?? o?.license_plate}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 4 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD/MM/YYYY"
                label={t('general.date', 'Date')}
                value={date ? dayjs(date) : null}
                onChange={(newValue) => setDate(newValue ? newValue.format('YYYY-MM-DD') : '')}
                disableFuture
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                disabled={!selectedVehicleId}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item size={{ xs: 12, md: 4 }}>
            <TextField
              label={t('tireCardHistory.kmUnit', 'KM Unit')}
              type="number"
              fullWidth
              value={kmUnit}
              onChange={(e) => setKmUnit(e.target.value)}
              disabled={!selectedVehicleId}
              error={kmUnit !== '' && !isValidKm}
              helperText={
                kmUnit !== '' && !isValidKm
                  ? limitInfo.errorRule ||
                    `${t('general.mustBe', 'Must be')} >= ${limitInfo.minKm} ${t('general.and', 'and')} <= ${limitInfo.maxKm === Infinity ? t('general.infinity', 'infinity') : limitInfo.maxKm}`
                  : limitInfo.errorRule || ''
              }
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 4 }}>
            <TextField
              label={t('tireCardHistory.hmUnit', 'HM Unit')}
              type="number"
              fullWidth
              value={hmUnit}
              onChange={(e) => setHmUnit(e.target.value)}
              disabled={!selectedVehicleId}
              error={hmUnit !== '' && !isValidHm}
              helperText={
                hmUnit !== '' && !isValidHm
                  ? limitInfo.errorRule ||
                    `${t('general.mustBe', 'Must be')} >= ${limitInfo.minHm} ${t('general.and', 'and')} <= ${limitInfo.maxHm === Infinity ? t('general.infinity', 'infinity') : limitInfo.maxHm}`
                  : limitInfo.errorRule || ''
              }
            />
          </Grid>
        </Grid>
      </MainCard>

      {selectedVehicleId && (
        <Box
          sx={{
            mb: 1,
            p: 2,
            bgcolor: '#e0f2fe',
            borderRadius: 2,
            border: '1px solid #bae6fd',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <InfoOutlinedIcon sx={{ color: '#0284c7' }} />
          <Box>
            <Typography variant="body2" sx={{ color: '#0369a1' }}>
              <strong>{t('tireCycle.infoUnit', 'Info Unit:')}</strong>
              {limitInfo.isBackdate ? (
                <>
                  <br />
                  {t('tireCycle.before', 'Before:')}{' '}
                  <strong>
                    {limitInfo.beforeData
                      ? `${limitInfo.beforeData.km_unit} KM / ${limitInfo.beforeData.hm_unit} HM (${limitInfo.beforeData.transaction_type || 'None'})`
                      : 'None'}
                  </strong>
                  <br />
                  {t('tireCycle.after', 'After:')}{' '}
                  <strong>
                    {limitInfo.afterData
                      ? `${limitInfo.afterData.km_unit} KM / ${limitInfo.afterData.hm_unit} HM (${limitInfo.afterData.transaction_type || 'None'})`
                      : 'None'}
                  </strong>
                </>
              ) : (
                <>
                  {t('tireCycle.lastKmAndHm', 'Last Km') + limitInfo.minKm + ' ' + t('tireCycle.kmAndLast', 'KM and Last Hour Meter was')}{' '}
                  {limitInfo.minHm} {t('tireCycle.hm', 'HM')}
                </>
              )}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Tabs */}
      <MainCard content={false} sx={{ mb: 1 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="tire cycle action tabs">
            <Tab label={t('tireCycle.installRemove', 'Install / Remove')} />
            <Tab label={t('tireCycle.inspection', 'Inspection')} />
          </Tabs>
        </Box>
      </MainCard>

      {/* Tab Content */}

      {/* ── Tab 0: Install / Remove ── */}
      {activeTab === 0 && (
        <Grid container spacing={3} alignItems="stretch">
          <Grid item size={{ xs: 12, md: 5 }} sx={{ display: 'flex', flexDirection: 'column' }}>
            {renderAxleMap()}
          </Grid>
          <Grid item size={{ xs: 12, md: 7 }} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
              {actionView === 'list' && isHmKmValid && (
                <TireActionList
                  vehicleDetails={vehicleDetails}
                  companyId={companyId}
                  positionMap={positionMap}
                  installedTires={installedTires}
                  onInstallClick={handleInstallClick}
                  onRemoveClick={handleRemoveClick}
                  selectedTire={selectedTire}
                  onSelectTire={setSelectedTire}
                  onTiresDataChange={handleTiresDataChange}
                />
              )}
              {actionView === 'list' && !isHmKmValid && (
                <Box sx={{ p: 4, textAlign: 'center', minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body1" color="warning.main" fontWeight="bold">
                    {t(
                      'tireCycle.provideValidHmKmActions',
                      'Please provide valid HM and KM units (greater than or equal to the previous records) to view the actions.'
                    )}
                  </Typography>
                </Box>
              )}

              {actionView === 'install' && (
                <InstallForm
                  vehicleDetails={vehicleDetails}
                  tireEquipVehicleId={tireEquipVehicleId}
                  selectedPosition={selectedPosition}
                  rawAxleData={rawAxleData}
                  hmUnit={hmUnit}
                  kmUnit={kmUnit}
                  date={date}
                  onSuccess={refreshMap}
                  onCancel={handleCancelAction}
                  positionMap={positionMap}
                  queue={installQueue}
                  setQueue={setInstallQueue}
                  limitInfo={limitInfo}
                />
              )}

              {actionView === 'remove' && (
                <RemoveForm
                  vehicleDetails={vehicleDetails}
                  tireEquipVehicleId={tireEquipVehicleId}
                  selectedTires={selectedTiresForRemove}
                  installedTires={installedTires}
                  rawAxleData={rawAxleData}
                  hmUnit={hmUnit}
                  kmUnit={kmUnit}
                  date={date}
                  onSuccess={refreshMap}
                  onCancel={handleCancelAction}
                  positionMap={positionMap}
                  queue={removeQueue}
                  setQueue={setRemoveQueue}
                  limitInfo={limitInfo}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      )}

      {/* ── Tab 1: Inspection ── */}
      {activeTab === 1 && (
        <Grid container spacing={1}>
          <Grid item size={{ xs: 12, md: 5 }}>
            {renderAxleMap()}
          </Grid>
          <Grid item size={{ xs: 12, md: 7 }}>
            <TabTireInspection
              vehicleDetails={vehicleDetails}
              companyId={companyId}
              installedTires={installedTires}
              positionMap={positionMap}
              hmUnit={hmUnit}
              kmUnit={kmUnit}
              date={date}
              selectedTire={selectedTire}
              onSelectTire={setSelectedTire}
              onTiresDataChange={handleTiresDataChange}
              isHmKmValid={isHmKmValid}
              limitInfo={limitInfo}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
